import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Size} from 'types/Theme'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {NoStyleButton} from '../helpers/NoStyleButton'
import {Icon} from '../Icon'

type StepperSize = Extract<Size, 'sm' | 'lg'>

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** ID of this component, passed down by the controlling parent component */
  id: string
  /** The maximum value allowed */
  max?: number
  /** The minimum value allowed */
  min?: number
  /** Name of this component, passed down by the controlling parent component */
  name: string
  /** An onChange callback passed down by the controlling parent component */
  onChange: (value: number) => void
  /** The value passed down by the controlling parent component */
  value: number
  /** The size of the component */
  size?: StepperSize
}

export const StepperButton = styled(NoStyleButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  outline: none;
  cursor: pointer;
  background: ${cssTheme.colors.stepper.default.background} none;
  border: ${cssTheme.layout.borderWidth.bw100} solid
    ${cssTheme.colors.stepper.default.border};
  color: ${cssTheme.colors.stepper.default.content};

  ${linariaMq.desktopXs} {
    &:hover {
      background-color: ${cssTheme.colors.stepper.hover.background};
      border-color: ${cssTheme.colors.stepper.hover.border};
      color: ${cssTheme.colors.stepper.hover.content};
    }

    &:focus-visible {
      z-index: 1;
      outline: 1px solid ${cssTheme.colors.border.interactive.primaryActive};
      outline-offset: 1px;
      background-color: ${cssTheme.colors.stepper.hover.background};
      color: ${cssTheme.colors.stepper.hover.content};
    }
  }

  &:disabled {
    cursor: not-allowed;
    background-color: ${cssTheme.colors.stepper.disabled.background};
    border-color: ${cssTheme.colors.stepper.disabled.border};
    color: ${cssTheme.colors.stepper.disabled.content};
  }
  &:first-of-type {
    border-start-start-radius: ${cssTheme.layout.radius.md};
    border-start-end-radius: 0;
    border-end-end-radius: 0;
    border-end-start-radius: ${cssTheme.layout.radius.md};
    border-width: ${cssTheme.layout.borderWidth.bw100};
  }
  &:last-of-type {
    border-start-start-radius: 0;
    border-start-end-radius: ${cssTheme.layout.radius.md};
    border-end-end-radius: ${cssTheme.layout.radius.md};
    border-end-start-radius: 0;
    border-width: ${cssTheme.layout.borderWidth.bw100};
  }
`

export const InputElement = styled.input`
  background-color: ${cssTheme.colors.stepper.default.background};
  border-top: ${cssTheme.layout.borderWidth.bw100} solid
    ${cssTheme.colors.stepper.default.border};
  border-bottom: ${cssTheme.layout.borderWidth.bw100} solid
    ${cssTheme.colors.stepper.default.border};
  border-right: 0;
  border-left: 0;
  color: ${cssTheme.colors.stepper.default.content};
  outline: none;
  text-align: center;
  cursor: default;
  border-radius: 0;
  padding: 0;
  ${cssTheme.typography.bodyS}
`

const StepperContainer = styled.div`
  display: flex;
  align-items: center;

  ${StepperButton} {
    height: 46px;
    width: 45px;
  }

  ${InputElement} {
    height: 46px;
    width: 46px;
  }

  &.--size-sm {
    ${StepperButton} {
      width: 33px;
      height: 34px;
    }

    ${InputElement} {
      width: 34px;
      height: 34px;
    }
  }
`

const isInRange = (value: number, min: number, max: number) =>
  value >= min && value <= max

export const Stepper = ({
  dataId,
  id,
  min = 0,
  max = 10,
  name,
  size,
  onChange,
  value,
  className
}: Props) => {
  const removeValue = () => {
    const newValue = value - 1

    if (isInRange(newValue, min, max)) {
      onChange(newValue)
    }
  }

  const addValue = () => {
    const newValue = value + 1

    if (isInRange(newValue, min, max)) {
      onChange(newValue)
    }
  }

  return (
    <StepperContainer
      data-id={dataId}
      className={cx(className, `--size-${size}`)}
    >
      <StepperButton
        data-id={`StepperMinus`}
        type="button"
        disabled={value <= min}
        onClick={removeValue}
        className={`--size-${size}`}
      >
        <Icon name="Minus" size="md" />
      </StepperButton>

      <InputElement
        readOnly
        id={id}
        name={name}
        type="text"
        value={value}
        className={`--size-${size}`}
      />

      <StepperButton
        data-id={`StepperPlus`}
        type="button"
        disabled={value >= max}
        onClick={addValue}
        className={`--size-${size}`}
      >
        <Icon name="Plus" size="md" />
      </StepperButton>
    </StepperContainer>
  )
}
